import { useState } from "react";
import { Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FormSection, TextAreaField, TextField } from "@/components/common/Forms";
import { PageTitle } from "@/components/shell/DashboardShell";
import { DetailGrid } from "@/components/portal/RequestParts";

const EDITABLE = ["contact_name", "designation", "phone", "address", "city", "state", "pincode", "website", "employee_count", "sites_count"];

export default function Profile() {
  const { user, setUser } = useAuth();
  const [f, setF] = useState(Object.fromEntries(EDITABLE.map((k) => [k, user[k] || ""])));
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/profile", f);
      setUser(data);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl" data-testid="portal-profile-page">
      <PageTitle eyebrow="Account" title="Company profile" />
      <div className="card-base mb-6 p-6" data-testid="profile-company-locked">
        <p className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-900"><Lock className="h-4 w-4 text-slate-400" /> Verified company details</p>
        <DetailGrid rows={[["Company", user.company_name], ["GSTIN / Reg. no.", user.gst_number], ["Company type", user.company_type], ["Industry", user.industry], ["Login email", user.email]]} />
        <p className="mt-4 text-xs text-slate-500">To change verified details, contact your Vinsafe account manager.</p>
      </div>
      <form onSubmit={submit} className="card-base space-y-8 p-6 sm:p-10" data-testid="profile-form">
        <FormSection step="01" title="Primary contact">
          <TextField id="profile-contact-name" label="Full name" value={f.contact_name} onChange={set("contact_name")} required />
          <TextField id="profile-designation" label="Designation" value={f.designation} onChange={set("designation")} required />
          <TextField id="profile-phone" label="Phone" value={f.phone} onChange={set("phone")} required />
          <TextField id="profile-website" label="Website" value={f.website} onChange={set("website")} />
        </FormSection>
        <FormSection step="02" title="Address & scale">
          <TextAreaField id="profile-address" label="Address" rows={2} value={f.address} onChange={set("address")} required className="col-span-full" />
          <TextField id="profile-city" label="City" value={f.city} onChange={set("city")} required />
          <TextField id="profile-state" label="State" value={f.state} onChange={set("state")} required />
          <TextField id="profile-pincode" label="PIN code" value={f.pincode} onChange={set("pincode")} required />
          <TextField id="profile-sites" label="Number of sites" value={f.sites_count} onChange={set("sites_count")} />
        </FormSection>
        <button type="submit" disabled={busy} className="btn-primary" data-testid="profile-save-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save changes</button>
      </form>
    </div>
  );
}
